import CommonConstants from "@/constants/common";
import http from "@/lib/http";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_ENDPOINT || "http://localhost:3000";

// Types
export interface AuthResponse {
  success: boolean;
  data?: {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
    resource_owner: {
      id: number;
      email: string;
      name: string;
      created_at: string;
    };
  };
  message?: string;
  errors?: string[] | { errors?: string[] } | string;
}

export interface UserInfo {
  id: number;
  email: string;
  name: string;
  created_at: string;
  updated_at: string;
  subscription_status: string;
  has_active_subscription: boolean;
  balance: number;
}

export interface UserInfoResponse {
  success: boolean;
  data?: {
    resource_owner: UserInfo;
    token_info: {
      access_token: string;
      expires_in: number;
      created_at: string;
    };
  };
  message?: string;
  errors?: string[];
}

export interface SubscriptionPackage {
  id: number;
  package_type: string;
  package_name: string;
  price: number;
  formatted_price: string;
  duration_days: number;
  formatted_duration: string;
  products_limit: number;
  search_limit: number;
  category_limit: number;
  features_summary: string;
  description: string;
  is_free_trial: boolean;
  is_active: boolean;
  user_count: number;
}

export interface SubscriptionPackageResponse {
  success: boolean;
  data?: SubscriptionPackage[];
  message?: string;
  errors?: string[];
}

export interface CurrentSubscription {
  id: number;
  package: {
    id: number;
    package_type: string;
    package_name: string;
    price: number;
    formatted_price: string;
    duration_days: number;
    formatted_duration: string;
    products_limit: number;
    description: string;
  };
  payment_status: string;
  can_access_products: boolean;
  created_at: string;
  latest_payment?: {
    id: number;
    price: number;
    formatted_price: string;
    status: string;
    paid_at: string;
    created_at: string;
  };
}

export interface CurrentSubscriptionResponse {
  success: boolean;
  data?: CurrentSubscription | null;
  message?: string;
  errors?: string[];
}

export interface PurchaseResponse {
  success: boolean;
  data?: {
    subscription: {
      id: number;
      payment_status: string;
      created_at: string;
    };
    payment: {
      id: number;
      price: number;
      status: string;
      paid_at?: string;
    };
    package: SubscriptionPackage;
  };
  message?: string;
  errors?: string[];
}

interface BackendResponse {
  success?: boolean;
  message?: string;
  errors?: string[];
  data?: unknown;
}

class ApiService {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  private getAuthHeaders(accessToken: string): Record<string, string> {
    return {
      Authorization: `Bearer ${accessToken}`,
    };
  }

  // Authentication methods
  async signUp(userData: {
    email: string;
    password: string;
    password_confirmation: string;
    name: string;
    tel: string;
  }): Promise<AuthResponse> {
    const response = await http.post<AuthResponse>(
      "/api/v1/tokens/sign_up",
      userData
    );
    // If http client returns success: false, the data contains the backend response
    // Backend response format: { success: false, errors: [...], message: ... }
    const backendResponse = response.data as AuthResponse;
    return backendResponse;
  }

  async signIn(credentials: {
    email: string;
    password: string;
  }): Promise<AuthResponse> {
    const response = await http.post<AuthResponse>(
      "/api/v1/tokens/sign_in",
      credentials
    );
    // If http client returns success: false, the data contains the backend response
    // Backend response format: { success: false, errors: [...], message: ... }
    const backendResponse = response.data as AuthResponse;
    return backendResponse;
  }

  async signOut(
    refreshToken: string
  ): Promise<{ success: boolean; message?: string }> {
    const response = await http.post("/api/v1/tokens/sign_out", {
      refresh_token: refreshToken,
    });
    return response.data as { success: boolean; message?: string };
  }

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    const response = await http.post<AuthResponse>("/api/v1/tokens/refresh", {
      refresh_token: refreshToken,
    });
    return response.data as AuthResponse;
  }

  async getUserInfo(accessToken: string): Promise<UserInfoResponse> {
    const response = await http.get<UserInfoResponse>(
      "/api/v1/tokens/info",
      this.getAuthHeaders(accessToken)
    );
    return response.data as UserInfoResponse;
  }

  // Subscription methods
  async getSubscriptionPackages(
    accessToken: string
  ): Promise<SubscriptionPackageResponse> {
    const response = await http.get<SubscriptionPackageResponse>(
      "/api/v1/subscriptions/packages",
      this.getAuthHeaders(accessToken)
    );
    return response.data as SubscriptionPackageResponse;
  }

  async getCurrentSubscription(
    accessToken: string
  ): Promise<CurrentSubscriptionResponse> {
    const response = await http.get<CurrentSubscriptionResponse>(
      "/api/v1/subscriptions/current_subscription",
      this.getAuthHeaders(accessToken)
    );

    // http.get returns: { data: {...}, success: true }
    // Backend returns: { success: true, data: {...}, message?: string }
    // So response.data contains the backend response: { success: true, data: {...} }
    const backendResponse = response.data;

    // If backendResponse is already in CurrentSubscriptionResponse format
    if (
      backendResponse &&
      typeof backendResponse === "object" &&
      "success" in backendResponse
    ) {
      return backendResponse as CurrentSubscriptionResponse;
    }

    // If backendResponse is the data directly (shouldn't happen but handle it)
    return {
      success: response.success,
      data: (backendResponse as unknown as CurrentSubscription) || null,
      message: response.message,
    } as CurrentSubscriptionResponse;
  }

  async purchaseSubscription(
    accessToken: string,
    subscriptionPackageId: number,
    paymentData?: {
      payment_method?: string;
      payment_gateway?: string;
      transaction_id?: string;
      amount?: number;
    }
  ): Promise<PurchaseResponse> {
    const requestBody = {
      subscription_package_id: subscriptionPackageId,
      ...paymentData,
    };

    const response = await http.post<PurchaseResponse>(
      "/api/v1/subscriptions/purchase",
      requestBody,
      this.getAuthHeaders(accessToken)
    );

    // http.post returns: { data: {...}, success: true }
    // Backend returns: { success: true, data: {...}, message?: string }
    // So response.data contains the backend response: { success: true, data: {...} }
    const backendResponse = response.data as PurchaseResponse | BackendResponse;

    // If backendResponse is already in PurchaseResponse format
    if (
      backendResponse &&
      typeof backendResponse === "object" &&
      "success" in backendResponse
    ) {
      return backendResponse as PurchaseResponse;
    }

    // Otherwise, wrap it
    const backend = backendResponse as BackendResponse;
    return {
      success: response.success,
      data: (backendResponse as PurchaseResponse["data"]) || undefined,
      message: backend?.message || response.message,
      errors: backend?.errors,
    } as PurchaseResponse;
  }

  async cancelSubscription(
    accessToken: string
  ): Promise<{ success: boolean; message?: string; errors?: string[] }> {
    const response = await http.delete<{
      success: boolean;
      message?: string;
      errors?: string[];
    }>("/api/v1/subscriptions/cancel", this.getAuthHeaders(accessToken));

    // http.delete returns: { data: {...}, success: true }
    // Backend returns: { success: true, data: {...}, message?: string }
    // So response.data contains the backend response: { success: true, data: {...} }
    const backendResponse = response.data as BackendResponse;

    // If backendResponse is already in correct format
    if (
      backendResponse &&
      typeof backendResponse === "object" &&
      "success" in backendResponse
    ) {
      return {
        success: backendResponse.success ?? true,
        message: backendResponse.message,
        errors: backendResponse.errors,
      };
    }

    // Otherwise, wrap it
    return {
      success: response.success,
      message: backendResponse?.message || response.message,
      errors: backendResponse?.errors,
    };
  }

  // Password reset methods
  async forgotPassword(email: string): Promise<{
    success: boolean;
    message?: string;
    errors?: string[];
  }> {
    const response = await http.post<{
      success: boolean;
      message?: string;
      errors?: string[];
    }>(CommonConstants.API_FORGOT_PASSWORD_PATH, { email });
    return response.data as {
      success: boolean;
      message?: string;
      errors?: string[];
    };
  }

  async resetPassword(
    email: string,
    temporaryPassword: string,
    newPassword: string,
    passwordConfirmation: string
  ): Promise<{
    success: boolean;
    message?: string;
    errors?: string[];
  }> {
    const response = await http.put<{
      success: boolean;
      message?: string;
      errors?: string[];
    }>(CommonConstants.API_RESET_PASSWORD_PATH, {
      email,
      temporary_password: temporaryPassword,
      new_password: newPassword,
      password_confirmation: passwordConfirmation,
    });
    return response.data as {
      success: boolean;
      message?: string;
      errors?: string[];
    };
  }
}

export const apiService = new ApiService();
